'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import ViralScoreMeter from './ViralScoreMeter';
import TrendsPanel from './TrendsPanel';

type Platform = 'tiktok' | 'instagram' | 'youtube' | 'twitter';

interface GenerationResult {
  id: string;
  topic: string;
  platform: string;
  hook: string;
  script: string;
  caption: string;
  hashtags: string[];
  viral_score: number;
  posting_time: string;
  status: string;
}

const PLATFORMS: { value: Platform; label: string; icon: string }[] = [
  { value: 'tiktok', label: 'TikTok', icon: '🎵' },
  { value: 'instagram', label: 'Instagram', icon: '📸' },
  { value: 'youtube', label: 'YouTube Shorts', icon: '▶️' },
  { value: 'twitter', label: 'Twitter/X', icon: '𝕏' },
];

export default function AutoGeneratePanel() {
  const [topic, setTopic] = useState('');
  const [platform, setPlatform] = useState<Platform>('tiktok');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<GenerationResult | null>(null);
  const [error, setError] = useState('');
  const [autoPost, setAutoPost] = useState(false);
  const [activeTab, setActiveTab] = useState<'script' | 'caption'>('script');

  const handleGenerate = async () => {
    if (!topic.trim()) return;
    setLoading(true);
    setError('');
    setResult(null);

    try {
      const res = await fetch('/api/auto-generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ topic, platform }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error ?? 'Generation failed');
      setResult(data.generation);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const handleTrendSelect = (keyword: string) => {
    setTopic(keyword);
  };

  return (
    <section id="generate" className="py-24 px-6" style={{ background: 'var(--blackout)' }}>
      <div className="max-w-6xl mx-auto">
        {/* Section header */}
        <div className="text-center mb-12">
          <div
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-semibold mb-4"
            style={{ background: 'rgba(194,24,91,0.12)', color: 'var(--ruby)', border: '1px solid rgba(194,24,91,0.25)' }}
          >
            <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" />
            V3 Content Factory
          </div>
          <h2 className="text-4xl font-bold font-display" style={{ color: 'var(--chrome)' }}>
            Auto-Generate Viral Content
          </h2>
          <p className="mt-3 text-base" style={{ color: 'var(--chrome-dim)' }}>
            Trend detection → Script → Video → Schedule. Fully automated.
          </p>
        </div>

        <div className="grid lg:grid-cols-5 gap-6">
          {/* Left: Generator */}
          <div className="lg:col-span-3 space-y-5">
            {/* Input card */}
            <div
              className="rounded-2xl p-6"
              style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}
            >
              <label className="block text-sm font-semibold mb-2" style={{ color: 'var(--chrome-dim)' }}>
                Topic or Niche
              </label>
              <div className="flex gap-3">
                <input
                  type="text"
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleGenerate()}
                  placeholder="e.g. fat loss, AI side hustle, crypto..."
                  className="flex-1 px-4 py-3 rounded-xl text-sm outline-none transition-all"
                  style={{
                    background: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    color: 'var(--chrome)',
                  }}
                  onFocus={(e) => (e.currentTarget.style.borderColor = 'rgba(194,24,91,0.5)')}
                  onBlur={(e) => (e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)')}
                />
              </div>

              {/* Platform selector */}
              <div className="mt-4">
                <label className="block text-sm font-semibold mb-2" style={{ color: 'var(--chrome-dim)' }}>
                  Platform
                </label>
                <div className="grid grid-cols-4 gap-2">
                  {PLATFORMS.map((p) => (
                    <button
                      key={p.value}
                      onClick={() => setPlatform(p.value)}
                      className="flex flex-col items-center gap-1 py-2.5 rounded-xl text-xs font-medium transition-all"
                      style={{
                        background: platform === p.value ? 'rgba(194,24,91,0.15)' : 'rgba(255,255,255,0.03)',
                        border: `1px solid ${platform === p.value ? 'rgba(194,24,91,0.4)' : 'rgba(255,255,255,0.07)'}`,
                        color: platform === p.value ? 'var(--ruby)' : 'var(--chrome-dim)',
                      }}
                    >
                      <span className="text-base">{p.icon}</span>
                      <span>{p.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Auto-post toggle */}
              <div className="mt-4 flex items-center justify-between p-3 rounded-xl"
                style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
                <div>
                  <p className="text-sm font-semibold" style={{ color: 'var(--chrome)' }}>Auto-Post</p>
                  <p className="text-xs" style={{ color: 'var(--chrome-dim)' }}>Automatically schedule after generation</p>
                </div>
                <button
                  onClick={() => setAutoPost(!autoPost)}
                  className="relative w-11 h-6 rounded-full transition-all flex-shrink-0"
                  style={{ background: autoPost ? 'var(--ruby)' : 'rgba(255,255,255,0.1)' }}
                >
                  <span
                    className="absolute top-0.5 w-5 h-5 rounded-full transition-all"
                    style={{
                      background: 'white',
                      left: autoPost ? '22px' : '2px',
                      boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
                    }}
                  />
                </button>
              </div>

              {/* Generate button */}
              <button
                onClick={handleGenerate}
                disabled={loading || !topic.trim()}
                className="w-full mt-4 py-3.5 rounded-xl font-semibold text-sm transition-all relative overflow-hidden"
                style={{
                  background: loading || !topic.trim()
                    ? 'rgba(194,24,91,0.3)'
                    : 'linear-gradient(135deg, var(--ruby) 0%, var(--ruby-dark) 100%)',
                  color: 'white',
                  border: '1px solid rgba(194,24,91,0.4)',
                  cursor: loading || !topic.trim() ? 'not-allowed' : 'pointer',
                }}
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeOpacity="0.3" />
                      <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                    </svg>
                    Generating Viral Content...
                  </span>
                ) : (
                  '⚡ Auto Generate Viral Video'
                )}
              </button>

              {error && (
                <p className="mt-3 text-xs text-center" style={{ color: '#ff7043' }}>{error}</p>
              )}
            </div>

            {/* Result card */}
            {result && (
              <div
                className="rounded-2xl p-6 space-y-5"
                style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(194,24,91,0.2)' }}
              >
                {/* Score + status row */}
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs font-semibold mb-1" style={{ color: 'var(--chrome-dim)' }}>
                      Viral Score
                    </p>
                    <ViralScoreMeter score={result.viral_score} size="md" />
                  </div>
                  <div className="text-right space-y-1">
                    <div
                      className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold"
                      style={{ background: 'rgba(194,24,91,0.12)', color: 'var(--ruby)', border: '1px solid rgba(194,24,91,0.25)' }}
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" />
                      {result.status === 'processing' ? 'Video Queued' : result.status}
                    </div>
                    <p className="text-xs" style={{ color: 'var(--chrome-dim)' }}>
                      Best time: {result.posting_time}
                    </p>
                  </div>
                </div>

                {/* Hook */}
                <div>
                  <p className="text-xs font-semibold mb-1.5" style={{ color: 'var(--chrome-dim)' }}>
                    🎣 Hook (0–3 sec)
                  </p>
                  <p
                    className="text-sm font-medium p-3 rounded-xl"
                    style={{ background: 'rgba(194,24,91,0.08)', color: 'var(--chrome)', border: '1px solid rgba(194,24,91,0.15)' }}
                  >
                    "{result.hook}"
                  </p>
                </div>

                {/* Tabs: Script / Caption */}
                <div>
                  <div className="flex gap-1 mb-3 p-1 rounded-xl" style={{ background: 'rgba(255,255,255,0.04)' }}>
                    {(['script', 'caption'] as const).map((tab) => (
                      <button
                        key={tab}
                        onClick={() => setActiveTab(tab)}
                        className="flex-1 py-1.5 rounded-lg text-xs font-semibold capitalize transition-all"
                        style={{
                          background: activeTab === tab ? 'rgba(194,24,91,0.2)' : 'transparent',
                          color: activeTab === tab ? 'var(--ruby)' : 'var(--chrome-dim)',
                        }}
                      >
                        {tab}
                      </button>
                    ))}
                  </div>
                  <div
                    className="text-sm p-3 rounded-xl whitespace-pre-line"
                    style={{ background: 'rgba(255,255,255,0.03)', color: 'var(--chrome)', border: '1px solid rgba(255,255,255,0.06)', minHeight: '80px' }}
                  >
                    {activeTab === 'script' ? result.script : result.caption}
                  </div>
                </div>

                {/* Hashtags */}
                <div className="flex flex-wrap gap-2">
                  {result.hashtags?.map((tag, i) => (
                    <span
                      key={i}
                      className="text-xs px-2.5 py-1 rounded-lg font-mono"
                      style={{ background: 'rgba(255,255,255,0.05)', color: 'var(--chrome-dim)', border: '1px solid rgba(255,255,255,0.08)' }}
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                {/* Action buttons */}
                <div className="flex gap-3">
                  <button
                    className="flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all"
                    style={{ background: 'rgba(194,24,91,0.12)', color: 'var(--ruby)', border: '1px solid rgba(194,24,91,0.25)' }}
                    onClick={() => {
                      const text = `Hook: ${result.hook}\n\nScript:\n${result.script}\n\nCaption: ${result.caption}\n\n${result.hashtags?.join(' ')}`;
                      navigator.clipboard?.writeText(text);
                    }}
                  >
                    Copy All
                  </button>
                  <Link
                    href="/preview"
                    className="flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all text-center"
                    style={{ background: 'linear-gradient(135deg, var(--ruby) 0%, var(--ruby-dark) 100%)', color: 'white', border: '1px solid rgba(194,24,91,0.4)' }}
                  >
                    {autoPost ? '📅 Preview & Schedule' : '👁 Preview & Post'}
                  </Link>
                </div>
              </div>
            )}
          </div>

          {/* Right: Trends panel */}
          <div className="lg:col-span-2">
            <TrendsPanel onSelectTrend={handleTrendSelect} />
          </div>
        </div>
      </div>
    </section>
  );
}
